import React from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, RefreshCw, FileText } from 'lucide-react';
import { errorLogger } from '../../utils/errorLogger';

export const AdminErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const error = useRouteError() as Error;

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    navigate('/admin');
  };

  const handleViewLogs = () => {
    const logs = errorLogger.getLogs();
    console.group('📋 Recent Error Logs');
    logs.forEach((log, index) => {
      console.log(`[${index + 1}] ${log.timestamp.toLocaleString()}: ${log.errorMessage}`);
    });
    console.groupEnd();
    alert('Error logs have been printed to the console. Press F12 to view.');
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 p-8 md:p-12">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <AlertTriangle className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Admin Panel Error
            </h1>

            <p className="text-lg text-gray-300 mb-8 max-w-md">
              An error occurred in the admin panel. This could be due to a data issue, network problem, or system error.
            </p>

            {error && (
              <div className="mb-8 w-full">
                <details className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 text-left">
                  <summary className="cursor-pointer font-semibold text-red-400 mb-2">
                    Technical Details
                  </summary>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Error Message:</p>
                      <p className="text-sm text-red-300 font-mono">{error.message}</p>
                    </div>
                    {error.stack && (
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Stack Trace:</p>
                        <pre className="text-xs text-red-300 overflow-auto max-h-32 font-mono bg-black/20 p-2 rounded">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mb-6">
              <button
                onClick={handleRetry}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 transition-all shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                Reload Page
              </button>

              <button
                onClick={handleGoBack}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg font-semibold border border-gray-600 hover:bg-gray-600 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Admin Home
              </button>
            </div>

            <button
              onClick={handleViewLogs}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-300 text-sm"
            >
              <FileText className="w-4 h-4" />
              View Error Logs
            </button>

            <div className="mt-8 pt-8 border-t border-gray-700 w-full">
              <p className="text-sm text-gray-400">
                If this error persists, please check your database connection and ensure all services are running properly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
